import React from 'react';
import { RotateCcw, RotateCw, Save, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { esc, printHtmlDocument } from '@/lib/printHtml';
import { documentPreviewKind, rotateDocumentFile } from '@/lib/documentRotation';

export function RotatableDocumentPreview({ file, url, document: record, onApply, autoApply = false, disabled = false, onBusyChange }) {
  const [rotatedFile, setRotatedFile] = React.useState(null);
  const [objectUrl, setObjectUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const generation = React.useRef(0);
  const kind = documentPreviewKind(file || record);
  React.useEffect(() => { onBusyChange?.(busy); return () => onBusyChange?.(false); }, [busy, onBusyChange]);
  React.useEffect(() => {
    generation.current += 1;
    setRotatedFile(null);
    setError('');
    setBusy(false);
    return () => { generation.current += 1; };
  }, [file, url]);
  React.useEffect(() => {
    const blob = rotatedFile || file;
    const next = blob ? URL.createObjectURL(blob) : '';
    setObjectUrl(next);
    return () => { if (next) URL.revokeObjectURL(next); };
  }, [rotatedFile, file]);
  const rotate = async (angle) => {
    if (busy || disabled) return;
    const version = generation.current;
    setBusy(true); setError('');
    try {
      let source = rotatedFile || file;
      if (!source) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('No se pudo descargar el documento para girarlo.');
        source = new File([await response.blob()], record?.file_name || (kind === 'pdf' ? 'documento.pdf' : 'imagen.png'), {
          type: record?.mime_type || (kind === 'pdf' ? 'application/pdf' : 'image/png'),
        });
      }
      const result = await rotateDocumentFile(source, angle);
      if (version !== generation.current) return;
      if (autoApply) await onApply(result);
      else setRotatedFile(result);
    } catch (err) {
      if (version === generation.current) setError(err.message || 'No se pudo girar el documento. Si es un enlace externo, descarga el archivo y adjúntalo.');
    } finally { if (version === generation.current) setBusy(false); }
  };
  const save = async () => {
    setBusy(true); setError('');
    try { await onApply(rotatedFile); }
    catch (err) { setError(err.message || 'No se pudo guardar el giro.'); }
    finally { setBusy(false); }
  };
  const printImage = async () => {
    if (busy || !(objectUrl || url)) return;
    setBusy(true); setError('');
    try {
      await printHtmlDocument(esc(record?.title || file?.name || 'Imagen del documento'),
        `<style>body { margin: 0; } .document-image { width: 190mm; height: 276mm; display: flex; align-items: center; justify-content: center; } .document-image img { max-width: 100%; max-height: 100%; object-fit: contain; }</style><div class="document-image"><img src="${esc(objectUrl || url)}" alt="${esc(record?.title || file?.name || 'Imagen del documento')}" /></div>`);
    } catch (err) { setError(err.message || 'No se pudo imprimir la imagen.'); }
    finally { setBusy(false); }
  };
  if (!kind) return null;
  const sourceUrl = objectUrl || url;
  return <div className="space-y-2">
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" disabled={busy || disabled || !sourceUrl} onClick={() => rotate(-90)}><RotateCcw className="mr-2 h-4 w-4" />Girar izquierda</Button>
      <Button type="button" variant="outline" disabled={busy || disabled || !sourceUrl} onClick={() => rotate(90)}><RotateCw className="mr-2 h-4 w-4" />Girar derecha</Button>
      {!autoApply && onApply && <Button type="button" disabled={!rotatedFile || busy || disabled} onClick={save}><Save className="mr-2 h-4 w-4" />Guardar giro</Button>}
      {kind === 'image' && <Button type="button" variant="outline" disabled={busy || !sourceUrl} onClick={printImage}><Printer className="mr-2 h-4 w-4" />Imprimir</Button>}
      {busy && <span className="text-sm" role="status">Preparando documento…</span>}
    </div>
    <p className="text-xs text-gray-600">{kind === 'pdf' ? 'El giro se aplica a todas las páginas. ' : ''}{autoApply ? 'El giro se conservará al guardar el documento.' : onApply ? 'Pulsa Guardar giro para conservar la orientación.' : 'El giro solo cambia esta vista.'}</p>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <div className="flex min-h-48 items-center justify-center overflow-auto rounded border bg-gray-50 p-2">
      {sourceUrl && (kind === 'pdf' ? <iframe title={record?.title || file?.name || 'Documento PDF'} src={sourceUrl} className="h-[55vh] w-full" />
        : <img src={sourceUrl} alt={record?.title || file?.name || 'Documento del proceso'} className="max-h-[55vh] max-w-full object-contain" />)}
    </div>
  </div>;
}
